'use client'

import { useAuthorization } from '@entities/authentication'
import { isKrProviderCancelled, type KrPaymentOrder, type PrepareKrPayment } from '@entities/kr-payment/model'
import { generateMerchantUid, usePaymentErrorStaticModel } from '@entities/payment'
import { userEntityQueries } from '@entities/users'
import {
  basePaymentRedirectedSearchParamsSchema,
  createVendorCustomerUid,
  FirstBillingPaymentRedirectedSearchParamsSchema,
  getCustomerUid,
  HandlePaymentQueue,
  LumpSumPaymentRedirectedSearchParamsSchema,
  PaymentActionState,
  PaymentCustomData,
  preserveMarketingParams,
  TrialPaymentRedirectedSearchParamsSchema,
  usePaymentByCardParking,
  usePaymentRedirectedSearchParams,
} from '@features/payment'
import {
  isPaymentError,
  normalizePortoneCurrency,
  requestIssueBillingKey,
  requestPayment,
} from '@features/payment/lib/portone-v2'
import {
  preparedCustomData,
  preparedPaymentUrl,
  requestPreparedProvider,
  preparedChannelKey,
  confirmPreparedPayment,
  confirmPreparedPaymentReturn,
  loadPreparedBillingReturn,
} from '@features/payment/lib/prepared-payment'
import { clearPaymentResponseParams, setParams } from '@features/payment/lib/redirect-params'
import { rpc } from '@server/infrastructure/rpc'
import { PaymentMessageBodyType } from '@server/schemas'
import { env, productionAppEnvSchema } from '@shared/config/env'
import { useTranslation } from '@shared/config/i18n'
import { dayjs, error, success } from '@shared/libs'
import { useNativeBridgeStore } from '@shared/models'
import { openAlertDialog } from '@shared/ui'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { useMemo, useRef } from 'react'

type UsePaymentByNaverPayProps = {
  validatePayment: () => boolean
  preparePayment?: PrepareKrPayment
  handlePaymentQueue: HandlePaymentQueue
  customerUid: Nullable<string>
  merchantName: string
  amount: number
  currency?: string
  customData: PaymentCustomData
  checkCouponIfUseCoupon?: () => Promise<void>
  paymentType: Extract<PaymentMessageBodyType, 'FIRST_BILLING' | 'TRIAL' | 'LUMP_SUM'>
  couponId: Nullable<string>
  billingFailSlackMessage: (errorCode?: string, errorMessage?: string) => string
  paymentWebhookUrl: URL
  installmentPeriod: number
  mobileRedirectSearchParams: Nullable<
    | Brand<Pick<TrialPaymentRedirectedSearchParamsSchema, 'trial_level' | 'subscribe_id'>, 'TRIAL'>
    | Brand<Pick<FirstBillingPaymentRedirectedSearchParamsSchema, 'coupon_id'>, 'FIRST_BILLING'>
    | Brand<Pick<LumpSumPaymentRedirectedSearchParamsSchema, 'coupon_id'>, 'LUMP_SUM'>
    | Brand<
        Pick<LumpSumPaymentRedirectedSearchParamsSchema, 'zip_code' | 'address' | 'detail_address' | 'coupon_id'>,
        'IPAD'
      >
  >
}

export const usePaymentByNaverPay = ({
  validatePayment,
  preparePayment,
  handlePaymentQueue,
  customerUid,
  merchantName,
  amount,
  currency = 'KRW',
  customData,
  checkCouponIfUseCoupon,
  paymentType,
  billingFailSlackMessage,
  paymentWebhookUrl,
  installmentPeriod,
  mobileRedirectSearchParams,
}: UsePaymentByNaverPayProps) => {
  const t = useTranslation()
  const { os, from } = useNativeBridgeStore()
  const authorization = useAuthorization()
  const { ERROR_MESSAGES } = usePaymentErrorStaticModel()
  const redirectedSearchParams = usePaymentRedirectedSearchParams()
  const { data: userInfo } = useQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: authorization.bearerToken }),
  })
  const searchParams = useSearchParams()

  const handlePaymentQueueRef = useRef(handlePaymentQueue)

  // paymentByRegisteredPaymentMethodsForRegularLesson을 useQuery 내부에서 안전하게 사용하기 위한 ref
  const paymentByRegisteredPaymentMethodsForRegularLessonRef = useRef<
    | ((
        params: {
          customerUid: string
          merchantName: string
          amount: number
          customData: PaymentCustomData
          noticeUrl: string
        },
        issuedOrder?: KrPaymentOrder | null,
      ) => Promise<void>)
    | null
  >(null)

  const impSuccess = searchParams?.get('imp_success')
  const errorMsg = searchParams?.get('error_msg')

  // V1/V2 호환 리다이렉트 조건 체크
  const conditionForHandlePaymentQueue = useMemo(() => {
    if (!redirectedSearchParams || isKrProviderCancelled(redirectedSearchParams)) {
      return false
    }

    if (
      redirectedSearchParams.__brand !== 'FIRST_BILLING' &&
      redirectedSearchParams.__brand !== 'TRIAL' &&
      redirectedSearchParams.__brand !== 'LUMP_SUM'
    ) {
      return false
    }

    if (
      redirectedSearchParams.pg_provider !== basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.NAVER_PAY
    ) {
      return false
    }

    // V1/V2 호환: imp_uid 또는 payment_id/paymentId 또는 billingKey/issue_id 확인
    const hasV1Params = redirectedSearchParams.imp_uid && redirectedSearchParams.merchant_uid
    const hasV2Params = redirectedSearchParams.payment_id || redirectedSearchParams.paymentId
    const hasBillingKeyParams =
      ('billingKey' in redirectedSearchParams && redirectedSearchParams.billingKey) ||
      ('issue_id' in redirectedSearchParams && redirectedSearchParams.issue_id)

    // FIRST_BILLING과 TRIAL은 billingKey 또는 issue_id만 있어도 유효함
    if (redirectedSearchParams.__brand === 'FIRST_BILLING' || redirectedSearchParams.__brand === 'TRIAL') {
      if (!hasV1Params && !hasV2Params && !hasBillingKeyParams) {
        return false
      }
    } else {
      // LUMP_SUM은 payment params 필수
      if (!hasV1Params && !hasV2Params) {
        return false
      }
    }

    if (!redirectedSearchParams.complete || !redirectedSearchParams.amount || !redirectedSearchParams.payment_type) {
      return false
    }

    // FIRST_BILLING과 TRIAL은 customer_uid, issue_id, billingKey 중 하나가 필요하지만, LUMP_SUM은 필요 없음
    if (redirectedSearchParams.__brand === 'FIRST_BILLING' || redirectedSearchParams.__brand === 'TRIAL') {
      const hasCustomerUid = 'customer_uid' in redirectedSearchParams && redirectedSearchParams.customer_uid
      const hasIssueId = 'issue_id' in redirectedSearchParams && redirectedSearchParams.issue_id
      const hasBillingKey = 'billingKey' in redirectedSearchParams && redirectedSearchParams.billingKey
      if (!hasCustomerUid && !hasIssueId && !hasBillingKey) {
        return false
      }
    }

    // imp_success가 false인 경우는 실패 케이스이므로 처리하지 않음
    if (impSuccess === 'false') {
      return false
    }

    return true
  }, [redirectedSearchParams, impSuccess])

  // 리다이렉트된 결제 실패 케이스 처리 (모바일 앱에서 m_redirect_url로 리다이렉트된 경우)
  const pgProvider = searchParams?.get('pg_provider')
  useQuery({
    queryKey: ['handleRedirectedPaymentFailureNaverPay', impSuccess, errorMsg, pgProvider],
    queryFn: async () => {
      if (impSuccess === 'false' && errorMsg) {
        openAlertDialog({ title: errorMsg })
        // Slack 실패 로깅은 사용자 흐름을 막지 않도록 await 하지 않는다
        void rpc.api.v1.payment['logging-payment-success']
          .$post(
            {
              json: {
                action: paymentType === 'TRIAL' ? 'SLACK_TRIAL_FAIL' : 'SLACK_FAIL',
                message: billingFailSlackMessage(undefined, errorMsg),
              },
            },
            {
              headers: {
                Authorization: `Bearer ${authorization.bearerToken}`,
              },
            },
          )
          .catch(() => {})
      }
      return null
    },
    enabled:
      pgProvider === basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.NAVER_PAY &&
      impSuccess === 'false' &&
      Boolean(errorMsg),
    retry: false,
  })

  // V1/V2 호환 impUid 추출 헬퍼
  const extractImpUid = (params: typeof redirectedSearchParams) => {
    if (!params) {
      return undefined
    }
    return params.imp_uid ?? params.tx_id ?? params.payment_id ?? params.paymentId
  }

  const extractMerchantUid = (params: typeof redirectedSearchParams) => {
    if (!params) {
      return undefined
    }
    return params.merchant_uid ?? params.payment_id ?? params.paymentId
  }

  useQuery({
    queryKey: [
      'conditionForHandleTrialPaymentQueueNaverPay',
      redirectedSearchParams?.__brand === 'TRIAL' ? redirectedSearchParams.issue_id : undefined,
      redirectedSearchParams?.purchase_order_id ?? 'legacy',
    ],
    queryFn: async () => {
      if (!redirectedSearchParams || redirectedSearchParams.__brand !== 'TRIAL' || !userInfo) {
        throw new Error(t('payment.errors.unsupportedPaymentMethod'))
      }

      const originalOrder = await loadPreparedBillingReturn(redirectedSearchParams, authorization.bearerToken)
      let billingKey: string

      // 포트원 V2 SDK가 리다이렉트 시 직접 전달하는 billingKey 우선 사용
      if ('billingKey' in redirectedSearchParams && redirectedSearchParams.billingKey) {
        billingKey = redirectedSearchParams.billingKey as string
      } else if (redirectedSearchParams.issue_id) {
        // 앱 환경: issue_id로 billingKey 조회
        billingKey = await fetchBillingKeyWithRetry(userInfo.id.toString(), redirectedSearchParams.issue_id)
      } else if (redirectedSearchParams.customer_uid) {
        // 기존 웹 환경 또는 하위 호환
        billingKey = redirectedSearchParams.customer_uid
      } else {
        throw new Error(t('payment.errors.missingIssueOrCustomerUid'))
      }

      // 조회된 billingKey로 실제 빌링 결제 수행
      if (!paymentByRegisteredPaymentMethodsForRegularLessonRef.current) {
        throw new Error(t('payment.errors.paymentHandlerNotInitialized'))
      }
      await paymentByRegisteredPaymentMethodsForRegularLessonRef.current(
        {
          customerUid: billingKey,
          merchantName: merchantName,
          amount: redirectedSearchParams.amount,
          customData: originalOrder
            ? {
                ...customData,
                subscribe_id: originalOrder.subscribeId,
                coupon_id: originalOrder.couponId ?? null,
              }
            : customData,
          noticeUrl: paymentWebhookUrl.toString(),
        },
        originalOrder,
      )

      return true
    },
    enabled:
      Boolean(userInfo) &&
      conditionForHandlePaymentQueue &&
      redirectedSearchParams?.__brand === 'TRIAL' &&
      redirectedSearchParams?.pg_provider ===
        basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.NAVER_PAY,
    retry: false,
    staleTime: Infinity,
  })

  useQuery({
    queryKey: [
      'conditionForHandleFirstBillingPaymentQueueNaverPay',
      redirectedSearchParams?.__brand === 'FIRST_BILLING' ? redirectedSearchParams.issue_id : undefined,
      redirectedSearchParams?.purchase_order_id ?? 'legacy',
    ],
    queryFn: async () => {
      if (!redirectedSearchParams || redirectedSearchParams.__brand !== 'FIRST_BILLING' || !userInfo) {
        throw new Error(t('payment.errors.unsupportedPaymentMethod'))
      }

      const originalOrder = await loadPreparedBillingReturn(redirectedSearchParams, authorization.bearerToken)
      let billingKey: string

      // 포트원 V2 SDK가 리다이렉트 시 직접 전달하는 billingKey 우선 사용
      if ('billingKey' in redirectedSearchParams && redirectedSearchParams.billingKey) {
        billingKey = redirectedSearchParams.billingKey as string
      } else if (redirectedSearchParams.issue_id) {
        // 앱 환경: issue_id로 billingKey 조회
        billingKey = await fetchBillingKeyWithRetry(userInfo.id.toString(), redirectedSearchParams.issue_id)
      } else if (redirectedSearchParams.customer_uid) {
        // 기존 웹 환경 또는 하위 호환
        billingKey = redirectedSearchParams.customer_uid
      } else {
        throw new Error(t('payment.errors.missingIssueOrCustomerUid'))
      }

      // 조회된 billingKey로 실제 빌링 결제 수행
      if (!paymentByRegisteredPaymentMethodsForRegularLessonRef.current) {
        throw new Error(t('payment.errors.paymentHandlerNotInitialized'))
      }
      await paymentByRegisteredPaymentMethodsForRegularLessonRef.current(
        {
          customerUid: billingKey,
          merchantName: merchantName,
          amount: redirectedSearchParams.amount,
          customData: originalOrder
            ? {
                ...customData,
                subscribe_id: originalOrder.subscribeId,
                coupon_id: originalOrder.couponId ?? null,
              }
            : customData,
          noticeUrl: paymentWebhookUrl.toString(),
        },
        originalOrder,
      )

      return true
    },
    enabled:
      Boolean(userInfo) &&
      conditionForHandlePaymentQueue &&
      redirectedSearchParams?.__brand === 'FIRST_BILLING' &&
      redirectedSearchParams?.pg_provider ===
        basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.NAVER_PAY,
    retry: false,
    staleTime: Infinity,
  })

  useQuery({
    queryKey: ['conditionForHandleLumpSumPaymentQueueNaverPay', redirectedSearchParams?.purchase_order_id ?? 'legacy'],
    queryFn: async () => {
      if (!redirectedSearchParams || redirectedSearchParams.__brand !== 'LUMP_SUM') {
        throw new Error(t('payment.errors.unsupportedPaymentMethod'))
      }

      await confirmPreparedPaymentReturn(redirectedSearchParams, authorization.bearerToken)

      return handlePaymentQueueRef.current({
        status: 'success',
        data: {
          impUid: extractImpUid(redirectedSearchParams)!,
          amount: redirectedSearchParams.amount,
          merchantUid: extractMerchantUid(redirectedSearchParams)!,
          customerUid: undefined,
        },
      })
    },
    enabled:
      conditionForHandlePaymentQueue &&
      redirectedSearchParams?.__brand === 'LUMP_SUM' &&
      redirectedSearchParams?.pg_provider ===
        basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.NAVER_PAY,
    retry: false,
  })

  const paymentByRegisteredPaymentMethodsForRegularLesson = usePaymentByCardParking({
    preparePayment,
    handlePaymentQueue,
    failSlackWebHook: async (_, errorMessage) => {
      openAlertDialog({ title: `${errorMessage}` })
    },
  })

  // ref 할당: useQuery 내부에서 안전하게 호출하기 위함
  paymentByRegisteredPaymentMethodsForRegularLessonRef.current = paymentByRegisteredPaymentMethodsForRegularLesson

  // V2 빌링키 조회 (issue_id 기반, 재시도 포함)
  const fetchBillingKeyWithRetry = async (
    customerId: string,
    issueId: string,
    maxRetries = 10,
    initialDelay = 500,
  ): Promise<string> => {
    let delay = initialDelay
    for (let i = 0; i < maxRetries; i++) {
      const response = await rpc.api.v1.payment['billing-key'].lookup.$post(
        { json: { customerId, issueId } },
        { headers: { Authorization: `Bearer ${authorization.bearerToken}` } },
      )

      if (!response.ok) {
        if (response.status >= 500 && i < maxRetries - 1) {
          await new Promise((resolve) => setTimeout(resolve, delay))
          delay = Math.min(delay * 1.5, 3000)
          continue
        }
        throw new Error(`Failed to fetch billing key: ${response.status}`)
      }

      const result = (await response.json()) as
        | { status: 'success'; billingKey: string }
        | { status: 'pending'; message: string }
        | { status: 'not_found'; message: string }
      if (result.status === 'success') {
        return result.billingKey
      }
      if (result.status === 'not_found') {
        throw new Error(t('payment.errors.billingKeyNotFound'))
      }
      await new Promise((resolve) => setTimeout(resolve, delay))
      delay = Math.min(delay * 1.5, 3000)
    }
    throw new Error(t('payment.errors.billingKeyLookupTimeout'))
  }

  const createMobileRedirectUrl = (issueId?: string): URL => {
    const mobileRedirectUrl = clearPaymentResponseParams(new URL(window.location.href))

    setParams(mobileRedirectUrl, {
      payment_type: mobileRedirectSearchParams?.__brand as PaymentMessageBodyType,
      complete: 'true',
      amount: amount.toString(),
      pg_provider: basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.NAVER_PAY,
    })

    // FIRST_BILLING과 TRIAL만 issue_id가 필요함 (V2 호환)
    if (
      issueId &&
      (mobileRedirectSearchParams?.__brand === 'FIRST_BILLING' || mobileRedirectSearchParams?.__brand === 'TRIAL')
    ) {
      setParams(mobileRedirectUrl, { issue_id: issueId })
    }

    if (mobileRedirectSearchParams?.__brand === 'TRIAL') {
      setParams(mobileRedirectUrl, {
        trial_level: mobileRedirectSearchParams.trial_level,
        subscribe_id: mobileRedirectSearchParams.subscribe_id,
      })
    }

    if (mobileRedirectSearchParams?.__brand === 'FIRST_BILLING') {
      setParams(mobileRedirectUrl, { coupon_id: mobileRedirectSearchParams.coupon_id })
    }

    if (mobileRedirectSearchParams?.__brand === 'LUMP_SUM' || mobileRedirectSearchParams?.__brand === 'IPAD') {
      if (mobileRedirectSearchParams.coupon_id) {
        setParams(mobileRedirectUrl, { coupon_id: mobileRedirectSearchParams.coupon_id })
      }
    }

    if (mobileRedirectSearchParams?.__brand === 'IPAD') {
      if (
        mobileRedirectSearchParams.zip_code &&
        mobileRedirectSearchParams.address &&
        mobileRedirectSearchParams.detail_address
      ) {
        setParams(mobileRedirectUrl, {
          zip_code: encodeURIComponent(mobileRedirectSearchParams.zip_code),
          address: encodeURIComponent(mobileRedirectSearchParams.address),
          detail_address: encodeURIComponent(mobileRedirectSearchParams.detail_address),
        })
      }
    }

    setParams(mobileRedirectUrl, { lang_type: userInfo?.langType ?? undefined })

    return preserveMarketingParams(mobileRedirectUrl)
  }

  const vendorCustomerUid = createVendorCustomerUid()

  /**
   * V2 SDK를 사용한 네이버페이 일시불 결제 (LUMP_SUM)
   */
  const handleLumpSumPayment = async (): Promise<'success' | 'error'> => {
    if (!userInfo) {
      throw new Error('User info not found')
    }

    const redirectUrlRequired = ['IOS', 'IPAD', 'ANDROID', 'ANDROID_TABLET'].includes(os ?? '') || from === 'APP'
    const prepared = await preparePayment?.(true)
    if (preparePayment && !prepared) {
      return success()
    }
    const merchantUid = prepared?.orderId ?? generateMerchantUid(userInfo.id)
    const mobileRedirectUrl = preparedPaymentUrl(createMobileRedirectUrl(), prepared)
    const finalAmount = redirectUrlRequired ? Number(mobileRedirectUrl.searchParams.get('amount')) : amount

    const buyerName = productionAppEnvSchema.safeParse(env.NEXT_PUBLIC_APP_ENV).success
      ? userInfo.name
      : `${userInfo.name}-${env.NEXT_PUBLIC_APP_ENV}`

    // 이용 완료일 계산
    const targetDate = dayjs().add(installmentPeriod, 'month')
    const useCfmYmdt = targetDate.format('YYYYMMDD')

    // V2 SDK requestPayment with bypass.naverpay
    const response = await requestPreparedProvider(
      () =>
        requestPayment({
          channelKey: preparedChannelKey(prepared, env.NEXT_PUBLIC_IMP_NAVER_PAY_CHANNEL_KEY),
          paymentId: merchantUid,
          orderName: merchantName,
          totalAmount: finalAmount,
          currency: normalizePortoneCurrency(currency),
          payMethod: 'EASY_PAY', // 네이버페이
          customer: {
            customerId: userInfo.id.toString(),
            email: userInfo.email,
            fullName: buyerName,
            phoneNumber: userInfo.phone,
          },
          customData: preparedCustomData(customData, prepared) as Record<string, unknown>,
          noticeUrls: [preparedPaymentUrl(paymentWebhookUrl, prepared).toString()],
          redirectUrl: redirectUrlRequired ? mobileRedirectUrl.toString() : undefined,
          // V2 네이버페이 전용 bypass 파라미터
          bypass: {
            naverpay: {
              useCfmYmdt: useCfmYmdt, // 이용 완료일 (YYYYMMDD)
              productItems: [
                // REQUIRED 필드
                {
                  categoryType: 'PRODUCT',
                  categoryId: 'DIGITAL_CONTENT',
                  uid: customData.subscribe_id ?? customData.product_id ?? 'podo-product',
                  name: merchantName,
                  count: 1,
                },
              ],
            },
          },
        }),
      preparePayment,
      prepared?.orderId,
    )

    if (!response || isPaymentError(response)) {
      if (response) {
        await preparePayment?.cancel?.(prepared?.orderId)
      }
      const errorMessage = response?.message ?? t('payment.alerts.paymentFailed')
      openAlertDialog({ title: errorMessage })
      // Slack 실패 로깅은 사용자 흐름을 막지 않도록 await 하지 않는다
      void rpc.api.v1.payment['logging-payment-success']
        .$post(
          {
            json: {
              action: 'SLACK_FAIL',
              message: billingFailSlackMessage(response?.code, errorMessage),
            },
          },
          {
            headers: {
              Authorization: `Bearer ${authorization.bearerToken}`,
            },
          },
        )
        .catch(() => {})
      return error()
    }

    // V2 response에서 txId를 impUid로 매핑
    await confirmPreparedPayment(prepared, response, authorization.bearerToken)

    const paymentState: PaymentActionState = {
      status: 'success',
      data: {
        impUid: response.txId ?? response.paymentId,
        amount: finalAmount,
        merchantUid: response.paymentId,
        customerUid: undefined, // LUMP_SUM은 customerUid 없음
      },
    }

    await handlePaymentQueue(paymentState)
    return success()
  }

  /**
   * V2 SDK를 사용한 네이버페이 빌링키 발급 + 초회 결제 (FIRST_BILLING, TRIAL)
   * NOTE: NaverpayIssueBillingKeyBypass는 useCfmYmdt, productItems를 지원하지 않음
   * 이 파라미터들은 서버 빌링 시 전달해야 함
   */
  const handleBillingPayment = async (): Promise<'success' | 'error'> => {
    if (!userInfo) {
      throw new Error('User info not found')
    }

    const redirectUrlRequired = ['IOS', 'IPAD', 'ANDROID', 'ANDROID_TABLET'].includes(os ?? '') || from === 'APP'
    const prepared = await preparePayment?.()
    if (preparePayment && !prepared) {
      return success()
    }
    const issueId = prepared?.issueId ?? vendorCustomerUid
    const mobileRedirectUrl = preparedPaymentUrl(createMobileRedirectUrl(issueId), prepared)

    const buyerName = productionAppEnvSchema.safeParse(env.NEXT_PUBLIC_APP_ENV).success
      ? userInfo.name
      : `${userInfo.name}-${env.NEXT_PUBLIC_APP_ENV}`

    // Step 1: 빌링키 발급
    // NOTE: NaverpayIssueBillingKeyBypass는 subMerchantName, subMerchantId만 지원
    // useCfmYmdt, productItems는 빌링키 발급 시 사용 불가
    const billingKeyResponse = await requestPreparedProvider(
      () =>
        requestIssueBillingKey({
          channelKey: env.NEXT_PUBLIC_IMP_NAVER_PAY_CHANNEL_KEY,
          billingKeyMethod: 'EASY_PAY',
          issueId: issueId,
          issueName: merchantName,
          customer: {
            customerId: userInfo.id.toString(),
            email: userInfo.email,
            fullName: buyerName,
            phoneNumber: userInfo.phone,
          },
          customData: preparedCustomData(customData, prepared) as Record<string, unknown>,
          noticeUrls: [preparedPaymentUrl(paymentWebhookUrl, prepared).toString()],
          redirectUrl: redirectUrlRequired ? mobileRedirectUrl.toString() : undefined,
        }),
      preparePayment,
      prepared?.orderId,
    )

    if (!billingKeyResponse || isPaymentError(billingKeyResponse)) {
      if (billingKeyResponse) {
        await preparePayment?.cancel?.(prepared?.orderId)
      }
      const errorMessage = billingKeyResponse?.message ?? t('payment.alerts.billingKeyIssueFailed')
      openAlertDialog({ title: errorMessage })
      // Slack 실패 로깅은 사용자 흐름을 막지 않도록 await 하지 않는다
      void rpc.api.v1.payment['logging-payment-success']
        .$post(
          {
            json: {
              action: paymentType === 'TRIAL' ? 'SLACK_TRIAL_FAIL' : 'SLACK_FAIL',
              message: billingFailSlackMessage(billingKeyResponse?.code, errorMessage),
            },
          },
          {
            headers: {
              Authorization: `Bearer ${authorization.bearerToken}`,
            },
          },
        )
        .catch(() => {})
      return error()
    }

    // Step 2: 발급받은 빌링키로 서버 빌링 결제
    // V2의 billingKey를 customerUid로 사용 (서버에서 V2 API로 결제 처리)
    try {
      await paymentByRegisteredPaymentMethodsForRegularLesson(
        {
          customerUid: billingKeyResponse.billingKey,
          merchantName,
          amount,
          customData,
          noticeUrl: paymentWebhookUrl.toString(),
        },
        prepared,
      )
      return success()
    } catch {
      await preparePayment?.cancel?.(prepared?.orderId)
      return error()
    }
  }

  const handlePaymentForNewNaverPay = async (): Promise<'success' | 'error'> => {
    if (!validatePayment() || !userInfo) {
      return error()
    }

    await checkCouponIfUseCoupon?.()

    const isLumpSum = paymentType === 'LUMP_SUM'

    if (isLumpSum) {
      return handleLumpSumPayment()
    } else {
      return handleBillingPayment()
    }
  }

  const handlePaymentForRegisteredNaverPay = async (): Promise<'success' | 'error'> => {
    if (!validatePayment()) {
      return error()
    }

    try {
      await checkCouponIfUseCoupon?.()

      await paymentByRegisteredPaymentMethodsForRegularLesson({
        customerUid: getCustomerUid(customerUid),
        merchantName,
        amount,
        customData,
        noticeUrl: paymentWebhookUrl.toString(),
      })

      return success()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.PAYMENT_FAILED
      openAlertDialog({ title: errorMessage })
      return error()
    }
  }

  return { handlePaymentForNewNaverPay, handlePaymentForRegisteredNaverPay }
}
